import { create } from "zustand"
import { persist } from "zustand/middleware"

export interface UserDetails {
  name: string
  email: string
  state: string
  city: string
  dateOfBirth: string
  gender: string
}

export interface ConditionAnswer {
  conditionId: string
  diagnosed: boolean
  diagnosedDate: string
  medications: string
}

export interface ConditionQuestion {
  id: string
  text: string
  type: "boolean" | "date" | "text"
}

export interface Condition {
  id: string
  name: string
  questions: ConditionQuestion[]
}

export interface Allergy {
  allergen: string
  severity: string
  medication: string
}

export interface Vitals {
  bloodPressure: string
  heartRate: string
  dontKnowVitals: boolean
}

export interface Pharmacy {
  id: string
  name: string
  address: string
}

interface BookingState {
  // Booking flow state
  showBookingStoreFlow: boolean
  bookingStep: number
  activeBookingMessageId: string | null
  editMode: boolean

  // User details
  userDetails: UserDetails
  firstName: string
  lastName: string

  // Pharmacy selection
  selectedPharmacy: string | null

  // Conditions
  selectedConditions: string[]
  conditionAnswers: ConditionAnswer[]
  currentConditionIndex: number

  // Vitals
  vitals: Vitals

  // Allergies
  allergies: Allergy[]
  newAllergy: Allergy
  showAllergyInputs: boolean
  addAnotherAllergySelected: boolean

  // Check-in answers
  checkInAnswers: Record<string, boolean>

  // Red flag questions
  redFlagAnswers: Record<string, boolean>
  showUrgentCare: boolean

  // Actions
  setShowBookingStoreFlow: (show: boolean) => void
  setBookingStep: (step: number) => void
  setActiveBookingMessageId: (id: string | null) => void
  setEditMode: (edit: boolean) => void
  setUserDetails: (details: Partial<UserDetails>) => void
  setFirstName: (name: string) => void
  setLastName: (name: string) => void
  updateNameFromFirstAndLast: () => void
  setSelectedPharmacy: (id: string | null) => void
  toggleCondition: (conditionId: string) => void
  setSelectedConditions: (conditions: string[]) => void
  updateConditionAnswer: (field: keyof ConditionAnswer, value: string | boolean) => void
  moveToNextCondition: () => void
  setVitals: (field: keyof Vitals, value: string | boolean) => void
  addAllergy: (allergy: Allergy) => void
  setNewAllergy: (allergy: Partial<Allergy>) => void
  setShowAllergyInputs: (show: boolean) => void
  setAddAnotherAllergySelected: (selected: boolean) => void
  setCheckInAnswer: (questionId: string, value: boolean) => void
  setRedFlagAnswer: (questionId: string, value: boolean) => void
  resetBooking: () => void
}

const initialUserDetails: UserDetails = {
  name: "",
  email: "",
  state: "",
  city: "",
  dateOfBirth: "",
  gender: "",
}

const initialVitals: Vitals = {
  bloodPressure: "",
  heartRate: "",
  dontKnowVitals: false,
}

const initialAllergy: Allergy = {
  allergen: "",
  severity: "",
  medication: "",
}

export const useBookingStore = create<BookingState>()(
  persist(
    (set, get) => ({
      // Booking flow state
      showBookingStoreFlow: true,
      bookingStep: 0,
      activeBookingMessageId: null,
      editMode: false,

      // User details
      userDetails: initialUserDetails,
      firstName: "",
      lastName: "",

      // Pharmacy selection
      selectedPharmacy: null,

      // Conditions
      selectedConditions: [],
      conditionAnswers: [],
      currentConditionIndex: 0,

      // Vitals
      vitals: initialVitals,

      // Allergies
      allergies: [],
      newAllergy: initialAllergy,
      showAllergyInputs: false,
      addAnotherAllergySelected: false,

      // Check-in answers
      checkInAnswers: {},

      // Red flag questions
      redFlagAnswers: {},
      showUrgentCare: false,

      // Actions
      setShowBookingStoreFlow: (show) => set({ showBookingStoreFlow: show }),
      setBookingStep: (step) => set({ bookingStep: step }),
      setActiveBookingMessageId: (id) => set({ activeBookingMessageId: id }),
      setEditMode: (edit) => set({ editMode: edit }),

      setUserDetails: (details) =>
        set({
          userDetails: { ...get().userDetails, ...details },
        }),

      setFirstName: (name) => set({ firstName: name }),
      setLastName: (name) => set({ lastName: name }),

      updateNameFromFirstAndLast: () => {
        const { firstName, lastName } = get()
        if (firstName && lastName) {
          set({
            userDetails: {
              ...get().userDetails,
              name: `${firstName} ${lastName}`,
            },
            editMode: false,
          })
        }
      },

      setSelectedPharmacy: (id) => set({ selectedPharmacy: id }),

      toggleCondition: (conditionId) => {
        const { selectedConditions } = get()
        if (selectedConditions.includes(conditionId)) {
          set({
            selectedConditions: selectedConditions.filter((id) => id !== conditionId),
          })
        } else {
          set({
            selectedConditions: [...selectedConditions, conditionId],
          })
        }
      },

      setSelectedConditions: (conditions) => set({ selectedConditions: conditions }),

      updateConditionAnswer: (field, value) => {
        const { conditionAnswers, currentConditionIndex, selectedConditions } = get()
        const newAnswers = [...conditionAnswers]
        const currentConditionId = selectedConditions[currentConditionIndex]
        const answerIndex = newAnswers.findIndex((a) => a.conditionId === currentConditionId)

        if (answerIndex !== -1 && currentConditionId) {
          const prev = newAnswers[answerIndex] as Partial<ConditionAnswer> || {};
          newAnswers[answerIndex] = {
            conditionId: currentConditionId,
            diagnosed: typeof prev.diagnosed === "boolean" ? prev.diagnosed : false,
            diagnosedDate: prev.diagnosedDate ?? "",
            medications: prev.medications ?? "",
            [field]: value,
          }
          set({ conditionAnswers: newAnswers })
        }
      },

      moveToNextCondition: () => {
        const { currentConditionIndex, selectedConditions } = get()
        if (currentConditionIndex < selectedConditions.length - 1) {
          set({ currentConditionIndex: currentConditionIndex + 1 })
          return true
        }
        return false
      },

      setVitals: (field, value) =>
        set({
          vitals: { ...get().vitals, [field]: value },
        }),

      addAllergy: (allergy) =>
        set({
          allergies: [...get().allergies, allergy],
          newAllergy: initialAllergy,
        }),

      setNewAllergy: (allergy) =>
        set({
          newAllergy: { ...get().newAllergy, ...allergy },
        }),

      setShowAllergyInputs: (show) =>
        set({
          showAllergyInputs: show,
          allergies: show ? get().allergies : [],
        }),

      setAddAnotherAllergySelected: (selected) =>
        set({
          addAnotherAllergySelected: selected,
          newAllergy: selected ? initialAllergy : get().newAllergy,
        }),

      setCheckInAnswer: (questionId, value) =>
        set({
          checkInAnswers: { ...get().checkInAnswers, [questionId]: value },
        }),

      setRedFlagAnswer: (questionId, value) => {
        const updatedAnswers = { ...get().redFlagAnswers, [questionId]: value }
        const showUrgent = Object.values(updatedAnswers).some((answer) => answer === true)

        set({
          redFlagAnswers: updatedAnswers,
          showUrgentCare: showUrgent,
        })
      },

      resetBooking: () =>
        set({
          showBookingStoreFlow: false,
          bookingStep: 0,
          activeBookingMessageId: null,
          editMode: false,
          userDetails: initialUserDetails,
          firstName: "",
          lastName: "",
          selectedPharmacy: null,
          selectedConditions: [],
          conditionAnswers: [],
          currentConditionIndex: 0,
          vitals: initialVitals,
          allergies: [],
          newAllergy: initialAllergy,
          showAllergyInputs: false,
          addAnotherAllergySelected: false,
          checkInAnswers: {},
          redFlagAnswers: {},
          showUrgentCare: false,
        }),
    }),
    {
      name: "alfie-booking-storage",
    },
  ),
)
